import { FC, useEffect, useMemo, useState } from 'react';
import { generatePath, Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useParkedRideBookingsConfirm } from 'api/parked-ride-bookings/parked-ride-bookings';
import { CaseTypeEnum, GlobalSettingEnum, ParkedRideBookingModel } from 'api/model';
import Modal from 'components/Modal';
import Input from 'components/inputs/Input';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import Select from 'components/inputs/Select';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Routes from 'constants/Routes';
import Roles, { includesRoles } from 'constants/Roles';
import useMe from 'contexts/authentication/useMe';
import useTranslations from 'contexts/basicData/useTranslations';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import useGlobalSettingsForm from 'pages/Settings/GlobalSettings/useGlobalSettingsForm';
import { showException } from 'utils/exception-helper';
import useRideWaitingList from './useRideWaitingList';

const Wrapper = styled.div`
  padding: 10px;
`;

// Bokningsfiltrets inställningar bor som en toolbar direkt ovanför väntlistan —
// det är här personalen står när kapaciteten ändras. Långa förklaringar ligger som
// title-tooltips (samma texter som i Globala inställningar-labelarna).
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  padding: 8px 10px;
  margin-bottom: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ToolbarItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: bold;
  white-space: nowrap;
`;

// display: contents så att fieldsetens barn deltar direkt i Toolbar-flexen.
// disabled på fieldseten inaktiverar alla formulärkontroller inuti (inputs,
// selects, MultiSelectens knappar) — readonly-läget för icke-SuperAdmins.
const ToolbarFieldset = styled.fieldset`
  display: contents;
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const ReadonlyNote = styled.span`
  color: #666;
  font-weight: normal;
`;

// Länkade ben (samma Ride-order, t.ex. hemleverans + inbyte) hålls ihop visuellt
// så att de hanteras tillsammans — raden som inleder en ny order får en tjockare
// topplinje (samma mönster som AssignmentsLists TableStylerWrapper).
export const orderGroupStartClassName = 'order-group-start';

const TableStylerWrapper = styled.div`
  display: contents;

  .${orderGroupStartClassName} {
    border-top: 3px solid ${({ theme }) => theme.colors.border.primary};
  }
`;

const RowButton = styled(Button)`
  padding: 2px 10px;
`;

const FieldLabel = styled.div`
  margin: 8px 0 2px;
  font-weight: bold;
`;

const EmptyState = styled.div`
  padding: 20px;
  color: #666;
`;

// 24-timmarsalternativ för stopptiderna i halvtimmessteg. Native <input type="time">
// visar AM/PM i webbläsare med engelskt UI-språk och kan inte tvingas till 24h —
// en select ger garanterad 24-timmarsvisning.
const cutoffTimeOptions = Array.from(
  { length: 48 },
  (_, i) =>
    `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`
);

// Endast dessa tre typer kan parkeras av bokningsfiltret.
const caseTypeLabels: Partial<Record<CaseTypeEnum, string>> = {
  [CaseTypeEnum.Purchase]: 'Inköp',
  [CaseTypeEnum.HomeDelivery]: 'Hemleverans',
  [CaseTypeEnum.TradeIn]: 'Inbyte',
};

/** DateTimeOffset (Date vid runtime via handleDates-mutatorn) → svensk väggtid "ÅÅÅÅ-MM-DD TT:mm". */
const toSwedishDateTimeString = (value: Date) =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);

/** DateTimeOffset → värde för <input type="datetime-local"> i svensk tid. */
const toDateTimeLocalValue = (value: Date) =>
  toSwedishDateTimeString(value).replace(' ', 'T');

/**
 * Nästföljande dags datum (svensk tid) kl 07:00 som "YYYY-MM-DD 07:00" — värdet
 * nödstopps-bocken skriver till RideBookingFilterBlockAllHomeDeliveriesUntil.
 * Datumräkningen görs på UTC-middag för att vara immun mot DST-övergångar.
 */
const nextDaySevenSwedish = () => {
  const todaySwedish = toSwedishDateTimeString(new Date()).slice(0, 10);
  const nextDay = new Date(
    Date.parse(`${todaySwedish}T12:00:00Z`) + 24 * 60 * 60 * 1000
  )
    .toISOString()
    .slice(0, 10);
  return `${nextDay} 07:00`;
};

const RideWaitingListPage: FC = () => {
  const navigate = useNavigate();
  const { waitingList, refetchWaitingList, loading } = useRideWaitingList();

  const confirmCall = useParkedRideBookingsConfirm();

  // Backend tillåter bara SuperAdmin att spara globala inställningar
  // (POST api/GlobalSettings/UpdateSetting) — spegla det i UI:t så att övriga
  // ser filterläget utan att kunna ändra.
  const me = useMe();
  const canEditFilterSettings = includesRoles([Roles.SuperAdmin], me?.roles);

  const {
    watch,
    updateglobalSettingsAsync,
    refetchSettings,
    makeGlobalSettingsInput,
    makeGlobalSettingsCheckbox,
    mkGlobalSettingsSelect,
  } = useGlobalSettingsForm();
  const { globalSettings: globalSettingsTranslations } = useTranslations();

  const { zipCodeAreaByName } = useZipCodeAreas();
  const zipCodeAreaNamesOptions = useMemo(() => {
    return Object.keys(zipCodeAreaByName ?? {})
      .filter((u) => u)
      .sort((a, b) => a.localeCompare(b))
      .map((zipCodeArea) => ({
        label: zipCodeArea,
        value: zipCodeArea,
      }));
  }, [zipCodeAreaByName]);

  const [bookTarget, setBookTarget] = useState<ParkedRideBookingModel | null>(
    null
  );
  const [bookTime, setBookTime] = useState('');

  useEffect(() => {
    refetchWaitingList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorterat på önskad tid, men länkade ben (samma RideOrderID) grupperas ihop.
  const sortedList = useMemo(() => {
    if (!waitingList) return null;
    const byOrder = new Map<number, ParkedRideBookingModel[]>();
    [...waitingList]
      .sort((a, b) => a.bookedTo.getTime() - b.bookedTo.getTime())
      .forEach((row) => {
        const group = byOrder.get(row.rideOrderID) ?? [];
        group.push(row);
        byOrder.set(row.rideOrderID, group);
      });
    return Array.from(byOrder.values()).flat();
  }, [waitingList]);

  const openBookModal = (row: ParkedRideBookingModel) => {
    setBookTarget(row);
    setBookTime(toDateTimeLocalValue(row.bookedTo));
  };

  const handleBook = async () => {
    if (!bookTarget || !bookTime) return;
    try {
      const caseId = await confirmCall.mutateAsync({
        data: {
          parkedRideBookingID: bookTarget.id,
          // Fältet är NAIV svensk väggtid (samma semantik som Rides BookedTo). Skicka den
          // naiva strängen som den är — ett Date-objekt hade serialiserats av axios som
          // UTC (toISOString) och förskjutit tiden för webbläsare utanför svensk tidszon.
          confirmedBookedToSwedish: `${bookTime}:00` as unknown as Date,
        },
      });
      setBookTarget(null);
      setBookTime('');
      await refetchWaitingList();
      // Öppna det skapade ärendet direkt — eventuella justeringar (adresser m.m.)
      // görs där, inte i väntlistan.
      navigate(generatePath(Routes.rideWaitingList.case, { id: String(caseId) }));
    } catch (error) {
      showException(error);
    }
  };

  const renderBookModal = () => {
    if (!bookTarget) return null;
    return (
      <Modal
        title={`Boka ${bookTarget.registrationNumber}`}
        onClose={() => setBookTarget(null)}
        buttons={[
          {
            label: 'Boka',
            onClick: handleBook,
            loading: confirmCall.isLoading,
            disabled: confirmCall.isLoading || !bookTime,
          },
        ]}
      >
        <div>
          Önskad tid: {toSwedishDateTimeString(bookTarget.bookedTo)} —{' '}
          {bookTarget.blockReasonText}
        </div>
        <FieldLabel>Tid (svensk tid)</FieldLabel>
        <Input
          type="datetime-local"
          value={bookTime}
          onChange={(ev) => setBookTime(ev.currentTarget.value)}
        />
        <FieldLabel>Vad händer?</FieldLabel>
        <div>
          Ärendet skapas med den valda tiden och bokaren får ett
          bekräftelse-SMS. Eventuella justeringar görs inne i ärendet efter
          bokningen.
        </div>
      </Modal>
    );
  };

  // Rader (utom den allra första) som inleder en ny Ride-order får grupplinjen.
  const orderGroupStartIds = useMemo(() => {
    const ids = new Set<number>();
    sortedList?.forEach((row, index) => {
      if (index > 0 && sortedList[index - 1].rideOrderID !== row.rideOrderID) {
        ids.add(row.id);
      }
    });
    return ids;
  }, [sortedList]);

  // Ingen kolumnsortering: raderna är förgrupperade per Ride-order (länkade ben
  // bredvid varandra) och ordnade på önskad tid — fri sortering skulle splittra grupperna.
  const columnSettings: ColumnSetting<
    ParkedRideBookingModel,
    Record<string, never>
  >[] = [
    {
      head: 'Regnr',
      width: 80,
      render: (row) => row.registrationNumber,
    },
    {
      head: 'Typ',
      width: 100,
      render: (row) => caseTypeLabels[row.caseTypeID] ?? String(row.caseTypeID),
    },
    {
      head: 'Önskad tid',
      width: 125,
      render: (row) => toSwedishDateTimeString(row.bookedTo),
    },
    {
      head: 'Från',
      width: 230,
      render: (row) =>
        `${row.fromName}${row.fromCity ? `, ${row.fromCity}` : ''}${
          row.fromCountyName ? ` (${row.fromCountyName})` : ''
        }`,
    },
    {
      head: 'Till',
      width: 200,
      render: (row) => `${row.toName}${row.toCity ? `, ${row.toCity}` : ''}`,
    },
    {
      head: 'Bokare',
      width: 180,
      render: (row) =>
        `${row.bookerName}${
          row.bookerPhoneNumber ? ` ${row.bookerPhoneNumber}` : ''
        }`,
    },
    {
      head: 'Orsak',
      width: 230,
      render: (row) => row.blockReasonText,
    },
    {
      head: 'Inkom',
      width: 125,
      render: (row) => toSwedishDateTimeString(row.created),
    },
    {
      head: 'Åtgärder',
      width: 90,
      render: (row) => (
        <RowButton onClick={() => openBookModal(row)}>Boka</RowButton>
      ),
    },
  ];

  // Spann-gräns som 24-timmars-select som sparar direkt vid val (samma mönster som
  // checkbox-fabriken). Tomt val ("–") = ingen gräns åt det hållet; båda tomma = dagen
  // fri. Ett lagrat värde utanför halvtimmesstegen visas som eget alternativ.
  const renderTimeSelect = (setting: GlobalSettingEnum) => {
    const current = watch(String(setting)) ?? '';
    return (
      <Select
        onChange={async (ev) => {
          await updateglobalSettingsAsync({
            key: setting,
            value: ev.currentTarget.value,
          });
          refetchSettings();
        }}
        value={current}
        width={90}
      >
        <option value="">–</option>
        {current !== '' && !cutoffTimeOptions.includes(current) && (
          <option value={current}>{current}</option>
        )}
        {cutoffTimeOptions.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </Select>
    );
  };

  // Nödstoppet lagras som "YYYY-MM-DD HH:mm" (svensk tid) — samma fasta format som
  // nu-strängen, så lexikografisk jämförelse avgör om stoppet fortfarande är aktivt.
  const nowSwedishString = toSwedishDateTimeString(new Date());
  const blockAllValue =
    watch(
      String(GlobalSettingEnum.RideBookingFilterBlockAllHomeDeliveriesUntil)
    ) ?? '';
  const blockAllActive =
    blockAllValue !== '' && blockAllValue > nowSwedishString;

  const renderFilterToolbar = () => (
    <Toolbar>
      <ToolbarFieldset disabled={!canEditFilterSettings}>
      <ToolbarItem
        title={
          globalSettingsTranslations[GlobalSettingEnum.RideBookingFilterEnabled]
        }
      >
        Filter aktivt
        {
          makeGlobalSettingsCheckbox(GlobalSettingEnum.RideBookingFilterEnabled)
            .value
        }
      </ToolbarItem>
      <ToolbarItem
        title={
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterBlockAllHomeDeliveriesUntil
          ]
        }
      >
        {blockAllActive
          ? `Hemleveranser stoppade till ${blockAllValue}`
          : 'Stoppa hemleveranser till 07:00'}
        <Checkbox
          checked={blockAllActive}
          onChange={async (ev) => {
            await updateglobalSettingsAsync({
              key: GlobalSettingEnum.RideBookingFilterBlockAllHomeDeliveriesUntil,
              value: ev.currentTarget.checked ? nextDaySevenSwedish() : '',
            });
            refetchSettings();
          }}
        />
      </ToolbarItem>
      <ToolbarItem
        title={
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterClosedFromCounties
          ]
        }
      >
        Stängda län (inköp)
        {
          mkGlobalSettingsSelect(
            GlobalSettingEnum.RideBookingFilterClosedFromCounties,
            zipCodeAreaNamesOptions,
            'string'
          ).value
        }
      </ToolbarItem>
      <ToolbarItem
        title={[
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterHomeDeliveryWeekdayOpenFrom
          ],
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterHomeDeliveryWeekdayOpenUntil
          ],
        ].join('\n')}
      >
        Bokningsbart vardag
        {renderTimeSelect(
          GlobalSettingEnum.RideBookingFilterHomeDeliveryWeekdayOpenFrom
        )}
        –
        {renderTimeSelect(
          GlobalSettingEnum.RideBookingFilterHomeDeliveryWeekdayOpenUntil
        )}
      </ToolbarItem>
      <ToolbarItem
        title={[
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterHomeDeliverySaturdayOpenFrom
          ],
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterHomeDeliverySaturdayOpenUntil
          ],
        ].join('\n')}
      >
        lördag
        {renderTimeSelect(
          GlobalSettingEnum.RideBookingFilterHomeDeliverySaturdayOpenFrom
        )}
        –
        {renderTimeSelect(
          GlobalSettingEnum.RideBookingFilterHomeDeliverySaturdayOpenUntil
        )}
      </ToolbarItem>
      <ToolbarItem
        title={[
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterHomeDeliverySundayOpenFrom
          ],
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterHomeDeliverySundayOpenUntil
          ],
        ].join('\n')}
      >
        söndag
        {renderTimeSelect(
          GlobalSettingEnum.RideBookingFilterHomeDeliverySundayOpenFrom
        )}
        –
        {renderTimeSelect(
          GlobalSettingEnum.RideBookingFilterHomeDeliverySundayOpenUntil
        )}
      </ToolbarItem>
      <ToolbarItem
        title={
          globalSettingsTranslations[
            GlobalSettingEnum.RideBookingFilterMaxAffectedDaysAhead
          ]
        }
      >
        Dagfönster (län)
        {
          makeGlobalSettingsInput(
            GlobalSettingEnum.RideBookingFilterMaxAffectedDaysAhead,
            'number',
            { min: 0 }
          ).value
        }
      </ToolbarItem>
      </ToolbarFieldset>
      {!canEditFilterSettings && (
        <ReadonlyNote>
          Endast SuperAdmin kan ändra inställningarna.
        </ReadonlyNote>
      )}
    </Toolbar>
  );

  const renderTable = () => {
    if (!sortedList) return <LoadingSpinner>Laddar...</LoadingSpinner>;
    if (sortedList.length === 0) {
      return <EmptyState>Väntlistan är tom.</EmptyState>;
    }

    return (
      <TableStylerWrapper>
        <Table<ParkedRideBookingModel, Record<string, never>>
          columnSettings={columnSettings}
          rowClassName={(row) =>
            [
              `parked-ride-booking-row-${row.id}`,
              ...(orderGroupStartIds.has(row.id)
                ? [orderGroupStartClassName]
                : []),
            ].join(' ')
          }
          rows={sortedList}
        />
      </TableStylerWrapper>
    );
  };

  return (
    <Wrapper>
      <Outlet />
      {renderFilterToolbar()}
      {loading && !sortedList && <LoadingSpinner>Laddar...</LoadingSpinner>}
      {renderTable()}
      {renderBookModal()}
    </Wrapper>
  );
};

export default RideWaitingListPage;
